import React, { useState, useEffect } from "react";
// eslint-disable-next-line
import { Link, useNavigate, useLocation as useLocationRouter } from "react-router-dom";
import userIcon from "../assets/images/user.png";
import { useAppContext } from "../context/AppContext";
import Loader from "./loader";
import ButtonLoader from "./buttonLoader";
import { useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../assets/css/style.css";
import Toast from "../components/Toast";
import Alert from "./Alert";
import ApiService from "../services/ApiService";
import { json } from "stream/consumers";
import { getOrCreateDeviceId } from "../utils/deviceId";

const Header: React.FC = () => {
  const navigate = useNavigate();
  const DeliveryID = localStorage.getItem('delivery_id');
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const sales_order_id = localStorage.getItem("sales_order_id");
  const vendor_id = localStorage.getItem("vendor_id");
  const api_token = localStorage.getItem("_token");
  const [header, setHeader] = useState(false);
  const [isIndex, setIsIndex] = useState(false);
  const location = useLocation();
  const locationRouter = useLocationRouter();
  const {
    purchaseOrder,
    setPurchaseOrder,
    delivery,
    setDelivery,
    setProgressDeliveries,
    progressDeliveries,
    attachments,
    setAttachments,
    setLoadItems,
    setToken,
    setLoads,
    photos,
    setPhotos,
    loads,
    meterialSo,
    materialfo,
    setMaterialfo,
    setMeterialSo,
    salesOrder,
    setSalesOrder,
  } = useAppContext();

  // Get current step for progress indicator
  const getCurrentStep = () => {
    const path = locationRouter.pathname;
    const stepMap: { [key: string]: string } = {
      '/': 'home',
      '/pick-up': 'pick-up',
      '/upload-weight-ticket': 'upload-weight-ticket',
      '/jobsite': 'jobsite',
      '/proof-of-dump-meterial': 'proof-of-dump-meterial',
      '/dump-site-photo-reason': 'proof-of-dump-meterial',
      '/digital-signature': 'digital-signature',
      '/complete-delivery': 'complete-delivery',
    };
    return stepMap[path] || 'home';
  };

  const currentStep = getCurrentStep();
  
  // Compact progress steps for header
  const headerSteps = [
    { key: 'home', label: 'Accept', num: 1 },
    { key: 'pick-up', label: 'Pick Up', num: 2 },
    { key: 'upload-weight-ticket', label: 'Ticket', num: 3 },
    { key: 'jobsite', label: 'Jobsite', num: 4 },
    { key: 'proof-of-dump-meterial', label: 'Dump', num: 5 },
    { key: 'digital-signature', label: 'Sign', num: 6 },
    { key: 'complete-delivery', label: 'Done', num: 7 },
  ];

  const getStepStatus = (stepKey: string) => {
    const currentStepIndex = headerSteps.findIndex(s => s.key === currentStep);
    const stepIndex = headerSteps.findIndex(s => s.key === stepKey);
    if (stepIndex < currentStepIndex) return 'completed';
    if (stepIndex === currentStepIndex) return 'active';
    return 'pending';
  };

  useEffect(() => {
    setHasFetched(false);
  }, [location.pathname]);

  useEffect(() => {
    setHeader(location.pathname !== "/driver-access" && location.pathname !== "/403" && location.pathname !== "/404");
    setIsIndex(location.pathname === "/");
  }, [location]);

  // Smart fresh data fetch - updates when needed, polls for attachments/loads changes
  useEffect(() => {
    if (!api_token || !sales_order_id) return;

    let isMounted = true;
    let refreshIntervalId: NodeJS.Timeout | null = null;
    let lastLoadsCount = loads?.total_loads;
    let lastCompletedCount = loads?.completed_loads;

    const fetchFreshData = async (skipHasFetched = false) => {
      if (api_token && (skipHasFetched || !hasFetched) && isMounted) {
        if (!skipHasFetched) {
          setHasFetched(true);
        }

        try {
          const deviceId = getOrCreateDeviceId();
          const Response = await ApiService.getFreshData({
            sales_order_id: sales_order_id,
            delivery_id: delivery?.id || DeliveryID || null,
            vendor_id: vendor_id || null,
            token: true,
            device_id: deviceId,
          });
          
          if (!isMounted) return;

          if (Response && Response.success) {
            if (Response.cancelled) {
              setToast({
                message: "This Order has been Cancelled.",
                type: "error",
              });
              localStorage.clear();
              return;
            }

            // Update all data
            setPurchaseOrder(Response.data.purchase_order);
            setLoads(Response.data.loads);
            setDelivery(Response.data.delivery);
            setLoadItems(Response.data.item_loads);
            setPhotos(Response.data.photos || []);
            setSalesOrder(Response.data.sales_order);
            setMeterialSo(Response.data.material_so);
            setMaterialfo(Response.data.material_fo);
            
            // Always update attachments (they can change from Zoho)
            setAttachments(Response.data.attachments || []);
            setProgressDeliveries(Response.data.progress);

            // Track changes in loads
            if (Response.data.loads) {
              lastLoadsCount = Response.data.loads.total_loads;
              lastCompletedCount = Response.data.loads.completed_loads;
            }

            if (Response.api_token) {
              setToken(Response.api_token);
              localStorage.setItem("_token", Response.api_token);
            }
            if (Response?.data?.delivery) {
              localStorage.setItem("delivery_id", Response?.data?.delivery?.id);
            }
          } else {
            setLoads(Response?.data?.loads);
          }
        } catch (error) {
          console.error("Error fetching fresh data:", error);
        }
      }
    };
    
    // Initial fetch on mount or pathname change
    if (!hasFetched) {
      fetchFreshData();
    }

    // Poll for fresh data every 30 seconds to catch:
    // - New attachments from Zoho
    // - Loads changes (when other drivers complete deliveries)
    // - Any other updates
    refreshIntervalId = setInterval(() => {
      if (!document.hidden) {
        fetchFreshData(true); // Skip hasFetched flag for polling
      }
    }, 30000);

    // Refresh when tab becomes visible (user returns to app)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchFreshData(true); // Always refresh when tab becomes visible
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      if (refreshIntervalId) {
        clearInterval(refreshIntervalId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.pathname, api_token, sales_order_id]);

  // useEffect(() => {
  //   const getProgressLoads = async () => {
  //     if(api_token){
  //     const Response = await ApiService.getProgressLoads({
  //       sales_order_id: sales_order_id,
  //       token: true,
  //     });
  //     console.log('Response',Response);
  //     setProgressDeliveries(Response?.data?.progress);
  //   }
  //   };
  //   getProgressLoads();
  // }, []);

  // Real-time progress loads fetching - poll frequently for multi-driver updates
  useEffect(() => {
    if (!api_token || !sales_order_id) return;

    let intervalId: NodeJS.Timeout | null = null;
    let isMounted = true;
    let lastProgress = progressDeliveries;

    const getProgressLoads = async () => {
      // Only fetch if page is visible (not in background)
      if (document.hidden || !isMounted) return;

      try {
        const Response = await ApiService.getProgressLoads({
          sales_order_id: sales_order_id,
          token: true,
        });
        
        if (isMounted && Response?.data?.progress !== undefined) {
          const newProgress = Response.data.progress;
          // Only update if value changed (avoid unnecessary re-renders)
          if (newProgress !== lastProgress) {
            setProgressDeliveries(newProgress);
            lastProgress = newProgress;
          }
        }
      } catch (error) {
        console.error("Error fetching progress loads:", error);
      }
    };

    // Initial fetch
    getProgressLoads();

    // Poll every 10 seconds for real-time updates (when other drivers accept deliveries)
    // This ensures users see updates within 10 seconds
    intervalId = setInterval(() => {
      getProgressLoads();
    }, 10000);

    // Also listen for page visibility changes to refresh when tab becomes active
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        getProgressLoads(); // Refresh immediately when tab becomes visible
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [api_token, sales_order_id]);

  // console.log('delivery',delivery);
  //  purchaseOrder,
  //     loads,
  //     delivery,
  //     photos,
  //     meterialSo,
  //     api_token,
  //     hasFetched,
  //     navigate
  // console.log("salesOrder", salesOrder);
  // console.log("purchaseOrder", purchaseOrder);
  // console.log("attachments", attachments);

  // Redirect to 403 page when unauthorized
  useEffect(() => {
    if (!api_token && header) {
      // Add a small delay to prevent redirect loops during page transitions
      // Also check if we're already on an error page or vendor access pages
      const currentPath = location.pathname;
      const isErrorPage = currentPath === "/403" || currentPath === "/404";
      const isVendorAccessPage = currentPath === "/vendor-access" || currentPath === "/driver-access" || currentPath.startsWith("/verify-po");
      
      if (!isErrorPage && !isVendorAccessPage) {
        const redirectTimer = setTimeout(() => {
          // Double-check token still doesn't exist (might have been set during navigation)
          if (!localStorage.getItem("_token")) {
            navigate("/403");
          }
        }, 500);
        
        return () => clearTimeout(redirectTimer);
      }
    }
  }, [api_token, header, navigate, location.pathname]);

  if (!header) return null;

  if (!api_token) {
    return null;
  }

  if (isIndex) {
    // useEffect(() => {
    if (loads?.total_loads && loads?.total_loads === loads?.completed_loads) {
      navigate("/no-another-delivery");
    }
    // }, [loads, navigate]);
  }
  const Info = {
    headerTitle: salesOrder?.so_data?.reference_number || "",
    totalLoads: loads?.total_loads,
    completedLoads: loads?.completed_loads,
  };
  return (
    <>
      <div className="container-fluid">
        <div className="row header_top">
          <div className="col-12 d-flex align-items-center justify-content-between mti-2">
            <div className="logo d-flex align-items-center gap-3">
              <div className="btn-group">
                <img
                  src={userIcon}
                  style={{ width: "40px" }}
                  className="rounded-circle"
                  alt="User"
                />
              </div>
              {/* <h6 className="mb-0">{Info.headerTitle}</h6> */}
              <h6 className="mb-0">
  {Info.headerTitle}
  {salesOrder?.order_number ? ` - SO# ${salesOrder.order_number}` : ""}
</h6>
            </div>
            <div className="d-flex justify-content-end align-items-center gap-3">
            {/* <div className="btn-group">
              <button
                className="text-white bg-transparent border-0  outline-0"
                type="button"
                id="notifMenu"
                onClick={() => getProgressLoads()}
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <i className="bi bi-info-circle fs-4"></i>
                  <i className="bi bi-bell-fill fs-4"></i>  
              </button>
              <div
                className="dropdown-menu border-0 outline-0 shadow dropdown-menu-start"
                aria-labelledby="notifMenu"
              > */}

            {/* <div className="dropdown-item mt-2">
                  <div className="row position-relative">
                    <div className="col-lg-12 mb-3">
                      <div className="detail_card loads">
                        <div className="circle">
                          {Info.completedLoads}/{Info.totalLoads}
                        </div>
                        <div className="label">Delivered Loads</div>
                      </div>
                    </div>
                    <hr />
                    <div className="col-lg-12">
                      <div className="detail_card progress">
                        <div className="circle">0</div>
                        <div className="label">{progressDeliveries ?? 0}</div>
                      </div>
                    </div>
                  </div>
                </div> */}
            {/* <hr /> */}
            {/* <a className="dropdown-item mt-2" href="#">
                  You Have Received a New Order #5437
                </a> */}
            {/* </div>
            </div> */}
            {/* <div className="row">
              <div className="col-md-6">
                <div className="d-flex align-items-center gap-3 justify-content-center">
                  <strong className="text-white">Loads</strong>
                  <span className="py-2 px-4 badge badge-dark text-dark bg-white">
                    {Info.completedLoads}/{Info.totalLoads}
                  </span>
                </div>
                
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center gap-3 justify-content-center">
                  <strong className="text-white">In Progress</strong>
                  <span className="py-2 px-4 badge badge-dark text-dark bg-white">
                    {progressDeliveries ?? 0}
                  </span>
                </div>
              </div>
            </div> */}
              <div className="header-stats">
                <div className="stat-card loads">
                  <div className="circle-sm">
                    {Info.completedLoads}/{Info.totalLoads}
                  </div>
                  <div className="label-sm">Delivered Loads</div>
                </div>

                <div className="stat-card progress">
                  <div className="circle-sm">{progressDeliveries ?? 0}</div>
                  <div className="label-sm">In Progress</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Compact Progress Indicator in Header */}
          <div className="col-12 mt-2">
            <div className="d-flex align-items-center justify-content-between" style={{ 
              padding: '8px 12px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '8px',
              gap: '4px',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}>
              {headerSteps.map((step) => {
                const status = getStepStatus(step.key);
                const isCompleted = status === 'completed';
                const isActive = status === 'active';
                
                return (
                  <React.Fragment key={step.key}>
                    <div 
                      className="d-flex flex-column align-items-center"
                      style={{
                        minWidth: window.innerWidth <= 768 ? '40px' : '50px',
                        flex: '0 0 auto',
                        padding: window.innerWidth <= 768 ? '0 2px' : '0',
                      }}
                    >
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: isCompleted ? '#28a745' : isActive ? '#E07B00' : 'rgba(255,255,255,0.3)',
                          color: isCompleted || isActive ? '#fff' : 'rgba(255,255,255,0.7)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: '12px',
                          marginBottom: '4px',
                          border: isActive ? '2px solid #fff' : 'none',
                          boxShadow: isActive ? '0 2px 8px rgba(224, 123, 0, 0.5)' : 'none',
                        }}
                      >
                        {isCompleted ? (
                          <i className="bi bi-check" style={{ fontSize: '16px' }}></i>
                        ) : (
                          <span>{step.num}</span>
                        )}
                      </div>
                      <span
                        style={{
                          fontSize: '9px',
                          color: isActive ? '#fff' : isCompleted ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.6)',
                          fontWeight: isActive ? '700' : '500',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {step.label}
                      </span>
                    </div>
                    {step.num < 7 && window.innerWidth > 768 && (
                      <div
                        style={{
                          width: '12px',
                          height: '2px',
                          backgroundColor: isCompleted ? '#28a745' : 'rgba(255,255,255,0.3)',
                          marginTop: '-16px',
                          flex: '0 0 auto',
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Header;
